import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { env } from '../config/env.js'
import { Api } from '../lib/api.js'

const AuthContext = createContext(null)

const TOKEN_KEY = 'clarity_token'

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>')
  return ctx
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null)
  const [error, setError] = useState(null)
  const [usage, setUsage] = useState(null)
  const [quotaTriggered, setQuotaTriggered] = useState(false)

  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY)
    if (t) setToken(t)
  }, [])

  const login = useCallback(async (password) => {
    setError(null)
    const url = (env.API_BASE_URL || '') + '/api/auth/gate'
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })
    if (!res.ok) {
      const msg = `Auth failed (${res.status})`
      setError(msg)
      throw new Error(msg)
    }
    const data = await res.json()
    sessionStorage.setItem(TOKEN_KEY, data.token)
    setToken(data.token)
    return data.token
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY)
    setToken(null)
    setUsage(null)
    setQuotaTriggered(false)
  }, [])

  const onUnauthorized = useCallback(() => { logout() }, [logout])

  const refreshUsage = useCallback(async () => {
    const t = sessionStorage.getItem(TOKEN_KEY)
    if (!t) return null
    const api = new Api({ baseURL: env.API_BASE_URL || '', getToken: () => t, onUnauthorized })
    try {
      const data = await api.me()
      setUsage(data)
      return data
    } catch {
      return null
    }
  }, [onUnauthorized])

  const onQuotaExceeded = useCallback(() => { setQuotaTriggered(true) }, [])
  const clearQuotaTriggered = useCallback(() => { setQuotaTriggered(false) }, [])

  return (
    <AuthContext.Provider value={{
      token, login, logout, onUnauthorized, error,
      usage, refreshUsage,
      quotaTriggered, onQuotaExceeded, clearQuotaTriggered,
    }}>
      {children}
    </AuthContext.Provider>
  )
}
